/**
 * Helpers for the (non-standard, Chromium-only) Network Information API.
 *
 * `navigator.connection` is not in the DOM lib typings and is absent in Firefox
 * and Safari, so access is guarded and every field falls back to null. The
 * values are coarse estimates (downlink is rounded and capped ~10 Mbps for
 * anti-fingerprinting), useful as a triage signal rather than a measurement —
 * e.g. correlating a slow link with stalled/failed clip playback.
 */

interface NetworkInformationLikeIProps {
  effectiveType?: string
  downlink?: number
  rtt?: number
}
type NavigatorWithConnection = Navigator & {
  connection?: NetworkInformationLikeIProps
  mozConnection?: NetworkInformationLikeIProps
  webkitConnection?: NetworkInformationLikeIProps
}

export interface NetworkInfoIProps {
  // 'slow-2g' | '2g' | '3g' | '4g'
  network_effective_type: string | null
  // Estimated download bandwidth in Mbps.
  network_downlink_mbps: number | null
  // Estimated round-trip time in ms.
  network_rtt_ms: number | null
}

export const getNetworkInfo = (): NetworkInfoIProps => {
  const nav =
    typeof navigator !== 'undefined'
      ? (navigator as NavigatorWithConnection)
      : null
  const connection =
    nav?.connection ?? nav?.mozConnection ?? nav?.webkitConnection ?? null
  return {
    network_effective_type: connection?.effectiveType ?? null,
    network_downlink_mbps:
      typeof connection?.downlink === 'number' ? connection.downlink : null,
    network_rtt_ms: typeof connection?.rtt === 'number' ? connection.rtt : null,
  }
}
